import React, { useCallback, useEffect, useState } from 'react';
import { Input, Select, Space, Spin, Alert } from 'antd';
import { useTranslation } from 'react-i18next';
import EmptyState from '../../../../components/EmptyState';
import AdminResponsiveList, { IAdminCardModel } from '../../../../components/Admin/AdminResponsiveList';
import { TRANSLATIONS } from '../../../../constants/translations';
import { translate } from '../../../../helpers/translationHelper';
import type { ColumnsType } from 'antd/es/table';

export interface ICatalogListShellProps<T extends object> {
  /** Loader for this tab, called again on every search/filter change. */
  load: (state?: string, search?: string) => Promise<T[]>;
  rowKey: keyof T & string;
  /** Filter values this tab supports, plus their labels. Omit for a tab with no states. */
  stateOptions?: { value: string; labelKey: string }[];
  icon: React.ReactNode;
  columns: ColumnsType<T>;
  toCard: (record: T) => IAdminCardModel;
  /** Bumping this reloads -- how an action inside a tab refreshes its own list. */
  reloadToken?: number;
}

/**
 * The five catalogue tabs are the same screen with different rows: load,
 * search, filter by state, then the shared responsive list. Written once
 * here so a tab is only its columns and its card, and so none of them can
 * quietly drift into a different loading or empty state than its neighbours.
 */
function CatalogListShell<T extends object>({
  load,
  rowKey,
  stateOptions,
  icon,
  columns,
  toCard,
  reloadToken,
}: ICatalogListShellProps<T>): React.ReactElement {
  const { t } = useTranslation();
  const [rows, setRows] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [state, setState] = useState<string | undefined>(undefined);
  const [search, setSearch] = useState<string | undefined>(undefined);

  const run = useCallback(() => {
    setLoading(true);
    setError(false);
    load(state, search)
      .then(setRows)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [load, state, search]);

  useEffect(() => {
    run();
  }, [run, reloadToken]);

  return (
    <div>
      <Space style={{ marginBottom: 16 }} wrap>
        <Input.Search
          allowClear
          placeholder={translate(t, TRANSLATIONS.ADMIN_CATALOG_SEARCH_PLACEHOLDER)}
          style={{ maxWidth: 280, width: '100%' }}
          onSearch={(value) => setSearch(value || undefined)}
        />
        {stateOptions && (
          <Select
            allowClear
            placeholder={translate(t, TRANSLATIONS.ADMIN_CATALOG_STATE_FILTER_ALL)}
            style={{ maxWidth: 240, width: '100%' }}
            value={state}
            onChange={setState}
            options={stateOptions.map((option) => ({ value: option.value, label: translate(t, option.labelKey) }))}
          />
        )}
      </Space>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 32 }}>
          <Spin />
        </div>
      ) : error ? (
        <Alert type="error" showIcon message={translate(t, TRANSLATIONS.ADMIN_CATALOG_LOAD_ERROR)} />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={icon}
          title={translate(t, TRANSLATIONS.ADMIN_CATALOG_EMPTY_TITLE)}
          description={translate(t, TRANSLATIONS.ADMIN_CATALOG_EMPTY_DESCRIPTION)}
        />
      ) : (
        <AdminResponsiveList<T>
          rowKey={rowKey}
          dataSource={rows}
          pagination={{ pageSize: 20 }}
          scroll={{ x: 'max-content' }}
          columns={columns}
          toCard={toCard}
        />
      )}
    </div>
  );
}

export default CatalogListShell;
